import { ApiTransport } from './ApiTransport.js';
import { ApiError, ApiResponseError } from '../errors/ApiError.js';
import { errorForStatus } from '../errors/normalizeApiFailure.js';
import { ErrorCode } from '../../core/errors/ErrorCodes.js';
import { buildUrl } from './buildUrl.js';
import { redact } from '../logging/redact.js';

// The real network transport. The ONLY place in the API layer that calls
// fetch() - the constraint that no UI component may call fetch() directly is
// enforced structurally by there being exactly one such call site.
export class HttpTransport extends ApiTransport {
  #config;
  #logger;
  #fetch;

  constructor({ config, services: { logger = null, fetch: fetchImpl = null } = {} } = {}) {
    super();
    this.#config = config;
    this.#logger = logger;
    // Injectable so tests drive it without touching globals, and so a host
    // application can supply its own instrumented fetch.
    this.#fetch = fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  async request({ method, path, query, body, headers = {}, timeout, signal, keepalive = false }) {
    const url = buildUrl(this.#config.baseUrl, path, query);
    const timeoutMs = timeout ?? this.#config.timeout;

    // Two independent reasons to abort - our timeout, and a caller-supplied
    // signal (widget destroy). Both must work, so they are linked rather than
    // one replacing the other.
    const controller = new AbortController();
    const onExternalAbort = () => controller.abort();
    signal?.addEventListener('abort', onExternalAbort);

    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const startedAt = Date.now();
    let response;

    try {
      response = await this.#fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        keepalive,
      });
    } catch (error) {
      // A timeout and a caller-driven cancellation both surface as an
      // AbortError; only the flag we set ourselves can tell them apart, and
      // they mean very different things to a caller.
      if (timedOut) {
        throw new ApiError(`API request timed out after ${timeoutMs}ms`, {
          code: ErrorCode.SDK_NETWORK_TIMEOUT,
          endpoint: path,
          recoverable: true,
          cause: error,
          context: { endpoint: path, timeoutMs },
        });
      }
      if (error?.name === 'AbortError') {
        throw new ApiError('API request was cancelled', {
          code: ErrorCode.SDK_NETWORK_ABORTED,
          endpoint: path,
          recoverable: false,
          cause: error,
          context: { endpoint: path },
        });
      }
      throw new ApiError('API request failed to reach the server', {
        code: ErrorCode.SDK_NETWORK_FAILURE,
        endpoint: path,
        recoverable: true,
        cause: error,
        context: { endpoint: path },
      });
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onExternalAbort);
    }

    const requestId = readRequestId(response);
    const durationMs = Date.now() - startedAt;
    const payload = await readJson(response, path);

    // Logged after parsing so the response body can be included; the logger
    // redacts credentials in both directions before anything is printed.
    this.#logger?.request({
      method,
      path,
      status: response.status,
      durationMs,
      requestId,
      // Redacted HERE, at the boundary, rather than inside ApiLogger - the
      // logger is injectable, so a host application can supply its own. If
      // masking lived only in our logger, a custom one would receive the raw
      // API key. A credential must never leave the transport.
      body: redact(body),
      response: redact(payload),
    });

    if (!response.ok) {
      throw errorForStatus(response.status, { endpoint: path, body: payload });
    }

    return { status: response.status, data: payload, requestId };
  }
}

// A 204, or an empty body on a fire-and-forget endpoint, is a legitimate
// success rather than malformed - so an empty body maps to null instead of
// throwing. A non-empty body that is not JSON is a genuine contract violation.
async function readJson(response, endpoint) {
  let text;
  try {
    text = await response.text();
  } catch (error) {
    throw new ApiResponseError('API response body could not be read', {
      status: response.status,
      endpoint,
      cause: error,
      context: { endpoint },
    });
  }

  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    // Only for a successful status is unparseable JSON worth raising as a
    // malformed-response error; on a failure status the status itself is the
    // more useful signal and errorForStatus() will throw next.
    if (!response.ok) return null;
    throw new ApiResponseError('API response was not valid JSON', {
      status: response.status,
      endpoint,
      cause: error,
      context: { endpoint, bodyPreview: text.slice(0, 120) },
    });
  }
}

function readRequestId(response) {
  try {
    return response.headers?.get?.('x-request-id') ?? null;
  } catch {
    return null;
  }
}
